import { captureException } from '@sentry/browser';
import { AxiosError } from 'axios';

import { TOrder } from '@/types/orders';
import { TQuestionnaire } from '@/types/questionnaires';

import { getHeaderAcceptLanguage, TPaymentSchedule, TResponsePayload, TStatusCode } from '@/shared/api';
import { PLATFORM_CODE, SERVER_ERROR, STATUS_CODE_OK, UNPROCESSABLE_CODE } from '@/shared/constants/settings';

import { ResourceService, TErrorData, TReadParamsProperties } from '../base-services/resource-service';

import { axiosClientSCClass } from './axios';

export const QuestionnairesService = ResourceService('v1/questionnaires', axiosClientSCClass);

export const QuestionnairesServiceV2 = ResourceService('v2/questionnaires', axiosClientSCClass);

type TResponseProcessingQuestionnaires = {
    status: string;
};

type TCreateQuestionnaireProps = {
    rentProductId: number;
    requestedLoanTerm: number;
    creditProductId: number;
    requestedLoanAmount: number;
    isBuyout: boolean;
    cpa: string;
    verificationCode: string;
};

export const getQuestionnaireInformation = async ({
    number,
    expand = ['repayments'],
}: {
    number: string;
    expand?: string[];
}): Promise<TQuestionnaire> => {
    const response = await QuestionnairesServiceV2.readOne<{ payload: TQuestionnaire }, TReadParamsProperties>({
        identifier: number,
        params: {
            expand,
        },
    });

    return response.payload;
};

export const getProcessingQuestionnaires = async (number: string): Promise<TResponseProcessingQuestionnaires[]> => {
    const response = await QuestionnairesServiceV2.readOne<
        { payload: TResponseProcessingQuestionnaires[] },
        TReadParamsProperties
    >({
        identifier: `${number}/processing-transactions`,
        params: {
            'per-page': 1,
        },
    });

    return response.payload;
};

export const createQuestionnaire = async ({
    creditProductId,
    rentProductId,
    requestedLoanAmount,
    requestedLoanTerm,
    isBuyout,
    cpa,
    verificationCode,
}: TCreateQuestionnaireProps): Promise<{ isSuccess: boolean; isTechnicalError: boolean; message: string }> => {
    let isSuccess = true;
    let isTechnicalError = false;
    let message = '';
    try {
        await QuestionnairesService.create<TQuestionnaire, TReadParamsProperties>({
            data: {
                creditProductId,
                rentProductId,
                requestedLoanAmount,
                requestedLoanTerm,
                isBuyout,
                salePointId: process.env.SALE_POINT_ID,
                platformCode: PLATFORM_CODE,
                cpa,
                verificationCode,
            },
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
    } catch (err) {
        isSuccess = false;
        const error = err as AxiosError;
        if (error?.response?.status !== UNPROCESSABLE_CODE) {
            isTechnicalError = true;
            captureException(error);
        }

        const errorData = error.response?.data as TErrorData;
        message = errorData?.message;
    }

    return {
        isSuccess,
        message,
        isTechnicalError,
    };
};

export const getQuestionnairePaymentSchedule = async (number: string): Promise<TPaymentSchedule[]> => {
    const response = await QuestionnairesServiceV2.readOne<{ payload: TPaymentSchedule[] }>({
        identifier: `${number}/payment-schedule`,
    });
    return response.payload;
};

export const getQuestionnaire = async (number: string): Promise<TOrder> => {
    const response = await QuestionnairesServiceV2.readOne<{ payload: TOrder }>({
        identifier: `${number}`,
        params: {
            platformCode: PLATFORM_CODE,
            classifiersGroups: ['ARENTA'],
            expand: ['rentProduct', 'documents', 'repayments', 'jurist', 'prolongations', 'product', 'rentLeaseInfo'],
        },
    });

    return response.payload;
};

export const cancelQuestionnaire = async (
    number: string,
    locale: string,
): Promise<{ errorMessage?: string } & TStatusCode> => {
    try {
        await QuestionnairesServiceV2.command<TResponsePayload<{ success: boolean }>, TReadParamsProperties>({
            command: `${number}/cancel`,
            headers: getHeaderAcceptLanguage(locale),
        });

        return { statusCode: STATUS_CODE_OK };
    } catch (e) {
        const error = e as AxiosError;
        const data = error.response?.data as { error: TErrorData };
        captureException(error);

        return {
            statusCode: error.response?.status ?? SERVER_ERROR,
            errorMessage: data?.error.message,
        };
    }
};
